import { useEffect, useLayoutEffect, useRef } from "react";
import { Location, useLocation, useNavigationType } from "react-router-dom";

// Where each page was scrolled to, by its history entry. A work opened over
// a page (its modal, App.tsx) belongs to the page beneath it, so the Library
// under a work keeps the Library's entry.
const positions = new Map<string, number>();

// How long Back waits for the page to grow tall enough to scroll back to.
const RESTORE_WITHIN_MS = 1000;

const pageKey = (location: Location): string =>
  (location.state?.background as Location | undefined)?.key ?? location.key;

/**
 * Scroll the way a multi-page site does: a new page opens at the top, and
 * Back returns to where its page was left. The browser can't do the second
 * for an app like this one: it restores before the page has rendered, onto
 * a document still too short to scroll. So this records the position as it
 * changes and restores it once the page is tall enough.
 */
export default function ScrollMemory(): null {
  const location = useLocation();
  const navigation = useNavigationType();
  const key = pageKey(location);
  const current = useRef(key);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
    const onScroll = () => positions.set(current.current, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const previous = current.current;
    if (previous === key) return;
    current.current = key;

    // A replaced entry is the same page re-addressed (the Library's
    // filters): it stays where it is.
    if (navigation === "REPLACE") {
      positions.set(key, positions.get(previous) ?? window.scrollY);
      return;
    }

    const target = navigation === "POP" ? (positions.get(key) ?? 0) : 0;
    const started = Date.now();
    let frame = 0;
    const stop = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
    };
    const restore = () => {
      const room = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      if (room >= target || Date.now() - started > RESTORE_WITHIN_MS) {
        window.scrollTo(0, Math.min(target, room));
        stop();
      } else {
        frame = requestAnimationFrame(restore);
      }
    };
    // Someone who starts scrolling while the page loads has taken over.
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    restore();
    return stop;
  }, [key, navigation]);

  return null;
}
